const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Émis sur window quand le serveur répond 401 : le contexte
 * d'authentification l'écoute et renvoie à la page de connexion, quelle que
 * soit la page qui a fait l'appel.
 */
export const EVENEMENT_SESSION_PERDUE = 'gi:session-perdue';

export async function apiFetch(path, options = {}) {
  // Les en-têtes de l'appelant s'ajoutent au Content-Type JSON, sans
  // l'écraser : posés après `...options`, sinon un en-tête supplémentaire
  // (tâche de fond) faisait perdre le JSON, et le serveur ne lisait plus
  // le corps.
  const { headers, ...reste } = options;
  const res = await fetch(`${API_URL}${path}`, {
    // Le cookie de session part avec chaque appel, y compris en
    // développement où l'API tourne sur un autre port que l'application.
    credentials: 'include',
    ...reste,
    headers: { 'Content-Type': 'application/json', ...(headers || {}) },
  });

  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event(EVENEMENT_SESSION_PERDUE));

    // Le backend renvoie { message } : sans ça, l'UI n'afficherait qu'un code
    // HTTP là où le serveur explique précisément ce qui bloque.
    let message = `API error: ${res.status}`;
    try {
      const corps = await res.json();
      if (corps?.message) message = corps.message;
    } catch {
      /* réponse non JSON : on garde le code HTTP */
    }
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }

  return res.json();
}

/* ------------------------------------------------------------------ */
/* Tâches longues : 202 { tacheId }, puis GET /taches/:id jusqu'au bout  */
/* ------------------------------------------------------------------ */

const EN_TETE_TACHE = { 'x-tache-asynchrone': '1' };
const DELAI_SONDAGE_MS = 1500;
const ATTENTE_MAX_MS = 15 * 60 * 1000;

function erreurDe(message, status) {
  const err = new Error(message);
  err.status = status;
  return err;
}

/**
 * Attend la fin d'une tâche et renvoie sa réponse, exactement comme si
 * l'appel avait été direct. Une erreur du serveur est relancée avec son
 * message et son code.
 */
export async function attendreTache(tacheId) {
  const debut = Date.now();
  let rates = 0;
  for (;;) {
    await new Promise((r) => setTimeout(r, DELAI_SONDAGE_MS));
    if (Date.now() - debut > ATTENTE_MAX_MS) {
      throw erreurDe('L’opération met trop de temps : réessayez plus tard.', 504);
    }
    let etat;
    try {
      const res = await fetch(`${API_URL}/taches/${tacheId}`, { credentials: 'include' });
      if (res.status === 401) window.dispatchEvent(new Event(EVENEMENT_SESSION_PERDUE));
      etat = await res.json();
      if (!res.ok) throw erreurDe(etat?.message || `API error: ${res.status}`, res.status);
      rates = 0;
    } catch (err) {
      // Une coupure passagère du réseau ne doit pas perdre une tâche qui tourne.
      if (err.status === 404 || rates >= 4) throw err;
      rates += 1;
      continue;
    }
    if (etat.etat === 'en_cours') continue;
    if (etat.etat === 'echec') throw erreurDe(etat.message || 'Opération en échec.', etat.statusCode || 500);
    if (etat.statusCode >= 400) {
      throw erreurDe(etat.corps?.message || `API error: ${etat.statusCode}`, etat.statusCode);
    }
    return etat.corps;
  }
}

/** Comme apiFetch, pour un appel long : lancé en tâche de fond puis attendu. */
export async function apiFetchLong(path, options = {}) {
  const corps = await apiFetch(path, {
    ...options,
    headers: { ...EN_TETE_TACHE, ...(options.headers || {}) },
  });
  return corps && corps.tacheId ? attendreTache(corps.tacheId) : corps;
}

/** Pour les envois multipart faits avec fetch : l'en-tête à joindre. */
export const EN_TETE_TACHE_ASYNCHRONE = EN_TETE_TACHE;
